import type { LinkObjectType, LinkType } from "../providers/types";

/** One way to link a record: a link type, and which end the record is. */
export interface LinkChoice {
  key: string;
  label: string;
  linkTypeId: string;
  /** True when the record is the link's from end. */
  isFrom: boolean;
  otherType: LinkObjectType;
}

const PICKABLE: string[] = ["contact", "company", "deal"];

const human = (id: string) => {
  const words = id.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The links a record of `objectType` can take part in. A directed type is
 * offered both ways round when both ends are this kind of record; an
 * undirected one once (the database stores it once, 0003).
 */
export function linkChoices(
  types: LinkType[],
  objectType: LinkObjectType,
): LinkChoice[] {
  const choices: LinkChoice[] = [];
  for (const type of types) {
    // Properties cannot be picked yet (W14 P3), so their link types wait.
    if (
      !PICKABLE.includes(type.from_object_type) ||
      !PICKABLE.includes(type.to_object_type)
    ) {
      continue;
    }
    const fromHere = type.from_object_type === objectType;
    const toHere = type.to_object_type === objectType;
    if (!type.directed) {
      if (fromHere || toHere) {
        choices.push({
          key: type.id,
          label: human(type.id),
          linkTypeId: type.id,
          isFrom: fromHere,
          otherType: fromHere ? type.to_object_type : type.from_object_type,
        });
      }
      continue;
    }
    if (fromHere) {
      choices.push({
        key: `${type.id}:from`,
        label: `${human(type.id)}: this → other`,
        linkTypeId: type.id,
        isFrom: true,
        otherType: type.to_object_type,
      });
    }
    if (toHere) {
      choices.push({
        key: `${type.id}:to`,
        label: `${human(type.id)}: other → this`,
        linkTypeId: type.id,
        isFrom: false,
        otherType: type.from_object_type,
      });
    }
  }
  return choices;
}

/** What each API error means to the person adding a link. */
export const LINK_ERRORS: Record<string, string> = {
  link_exists: "Those two are already linked that way",
  link_to_self: "A record cannot be linked to itself",
  not_found: "That record could not be found",
};
